/**
 * The .ae01.json package the AutoCAD AE01 plugin reads. Field names are snake_case on purpose:
 * the plugin binds them by name, and a property it splits at a case change silently fails to bind.
 * Ported from TemplateCreator's models.py.
 */

/** "blocking" means no package may be built: POST /templatecreator/build refuses one that has any. */
export type ReviewSeverity = "warning" | "review" | "blocking";

export interface ReviewItem {
  severity: ReviewSeverity;
  category: string;
  message: string;
  source: string;
}

export interface DiagramPlacement {
  diagram_id: string;
  source_path: string;
  x: number;
  y: number;
  page_index: number;
  reason: string;
  attributes: Record<string, string>;
  /** Burst into model space once inserted (the Palm Beach riser and wiring view pairs). */
  explode_after_insert?: boolean;
}

export interface StarterPage {
  page_index: number;
  kind: string;
  source: string;
  x: number;
  y: number;
}

export interface RiserInstruction {
  closet_id: string;
  j_diagram_number: string;
  n_diagram_number: string;
  network_diagram_source: string;
  rack_diagram_source: string;
  ports_utilized: number;
  network_switches_utilized: number;
  cza_count: number;
  device_counts: Record<string, number>;
  device_blocks: Record<string, unknown>[];
}

export interface NetworkPortOffset {
  x: number;
  y: number;
  tag_side: string | null;
}

export interface DeviceRuleEntry {
  logical_name: string;
  path: string;
  side: string;
  height: number;
  counts_as_network_port: boolean;
  explode_after_insert: boolean;
  width: number;
  network_ports: NetworkPortOffset[];
}

export interface NetworkKeynote {
  text: string;
  trigger_blocks: string[];
}

export interface NetworkRiserConfig {
  device_dictionary: DeviceRuleEntry[];
  left_order: string[];
  right_order: string[];
  left_x_offset: number;
  left_y_gap: number;
  right_x_offset: number;
  right_y_gap: number;
  device_stack_y_offset: number;
  network_switch_port_capacity: number;
  mdf_high_cza_threshold: number;
  idf_high_cza_threshold: number;
  rack_diagram_mdf_high_cza: string;
  rack_diagram_mdf_default: string;
  rack_diagram_idf_high_cza: string;
  rack_diagram_idf_default: string;
  con_schedule_existing_path: string;
  con_schedule_ae_path: string;
  switch_retrofit_path: string;
  switch_construction_path: string;
  switch_retrofit_width: number;
  switch_construction_width: number;
  con_name_path: string;
  con_gennote_path: string;
  con_gennote_general_text: string;
  con_gennote_keynotes: NetworkKeynote[];
  diagram_blank_template: string;
  riser_grid_width: number;
  riser_grid_height: number;
  rack_grid_width: number;
  rack_grid_height: number;
  grid_columns: number;
  grid_rows: number;
  slot_width: number;
  slot_height: number;
  gap_x: number;
  gap_y: number;
  first_slot_x_offset: number;
  top_row_y: number;
  page_pitch: number;
  riser_start_page_index: number;
  connection_tag_left_path: string;
  connection_tag_right_path: string;
  rack_template_path: string;
  rack_overflow_template_path: string;
  rack_epic_devices: string[];
  rack_district_view_devices: string[];
  rack_barionet_devices: string[];
}

export interface LoudspeakerRiserConfig {
  device_dictionary: { logical_name: string; path: string }[];
}

export interface QuantityLegendConfig {
  device_display_names: Record<string, string>;
  title: string;
  header_equipment: string;
  header_qty: string;
  title_row_height: number;
  header_row_height: number;
  data_row_height: number;
  equipment_column_width: number;
  qty_column_width: number;
  data_text_height: number;
  insertion_x: number;
  insertion_y: number;
  gap_below_scope_of_work: number;
}

export interface SymbolLegendRow {
  symbol_block: string | null;
  description: string | null;
  manufacturer_part: string | null;
  height_roughin: string | null;
  section: string | null;
  always: boolean;
  triggers: string[];
  /** Draw symbol_block with its lines moved onto this layer. Only Palm Beach County packages set it. */
  icon_layer?: string;
}

export interface SymbolLegendConfig {
  entries: SymbolLegendRow[];
  title: string;
  header_labels: string[];
  title_row_height: number;
  header_row_height: number;
  section_row_height: number;
  data_row_height: number;
  icon_column_width: number;
  description_column_width: number;
  manufacturer_column_width: number;
  height_column_width: number;
  data_text_height: number;
  header_text_height: number;
  section_text_height: number;
  insertion_x: number;
  insertion_y: number;
  existing_block_name: string;
  existing_legacy_block_name: string;
}

export interface LoudspeakerScheduleRow {
  symbol_block: string | null;
  height: string | null;
  width: string | null;
  diameter: string | null;
  depth: string | null;
  shape: string | null;
  wattage_tap: string | null;
  installation: string | null;
  always: boolean;
  triggers: string[];
  /** As SymbolLegendRow.icon_layer. */
  icon_layer?: string;
}

export interface LoudspeakerScheduleConfig {
  entries: LoudspeakerScheduleRow[];
  title: string;
  title_row_height: number;
  header_row_height: number;
  header_row2_height: number;
  data_row_height: number;
  icon_column_width: number;
  height_column_width: number;
  width_column_width: number;
  diameter_column_width: number;
  depth_column_width: number;
  shape_column_width: number;
  wattage_column_width: number;
  installation_column_width: number;
  data_text_height: number;
  header_text_height: number;
  footnote_text: string;
  gap_below_symbol_legend: number;
  insertion_x: number;
  insertion_y: number;
  existing_block_name: string;
}

export interface SheetIndexConfig {
  title: string;
  title_row_height: number;
  data_row_height: number;
  blank_row_height: number;
  blank_line_between_rows: boolean;
  min_bottom_y: number;
  sheet_column_width: number;
  description_column_width: number;
  two_up_sheet_column_width: number;
  two_up_description_column_width: number;
  data_text_height: number;
  gap_below_quantity_legend: number;
  sheet_number_prefix: string;
  cover_label: string;
  symbols_label: string;
  generated_page_label: string;
  diagram_name_text_style: string;
  diagram_name_layer: string;
}

export interface ScopeOfWorkTableConfig {
  title: string;
  title_row_height: number;
  body_row_height: number;
  column_width: number;
  body_text_height: number;
  insertion_x: number;
  insertion_y: number;
  existing_block_name: string;
}

/** AV001's ZONES table on a Palm Beach County project: one colour swatch and name per zone, two columns. */
export interface ZonesTableConfig {
  title: string;
  title_row_height: number;
  data_row_height: number;
  column_width: number;
  text_height: number;
  swatch_width: number;
  swatch_height: number;
  swatch_inset_x: number;
  swatch_layer: string;
  swatch_transparency: number;
  swatch_linetype_scale: number;
  zone_hatch_layer_contains: string;
  gap_below_quantity_legend: number;
}

/** ProjectFacts as the plugin sees it inside the package - snake_case, every field present. */
export interface ManifestProject {
  project_name: string;
  district: string;
  address: string;
  project_type: string;
  network_mode: string;
  selected_speakers: string[];
  devices: { logical_name: string; network_id: string; quantity: number; amp_id: string; source: string }[];
  flags: Record<string, boolean>;
  source_dwg: string;
  tlx_path: string;
  order_quote_path: string;
  order_products: string[];
  order_product_quantities: Record<string, number>;
  order_read_ok: boolean;
  order_read_message: string;
  solutions: string[];
  manual_diagram_quantities: Record<string, number>;
  two_way_system_type: string;
  two_way_system_strobe: boolean;
  poe_surge_protectors: boolean;
  safe_dongles: boolean;
  detected_blocks: Record<string, number>;
  dwg_scan_available: boolean;
  volume_control_zones: string[];
  general_notes_found: boolean | null;
}

/** One zone's loudspeaker-run attachments, in the order the run places them. */
export interface ZoneAttachmentEntry {
  zone: string;
  terminal_block: boolean;
  surge_protector: boolean;
}

export interface BuildManifest {
  schema_version: string;
  /** The oldest AutoCAD plugin that can render this package. AE01 stops when the installed one is older. */
  required_connector_version: string;
  project: ManifestProject;
  template_dwt: string;
  output_dwg: string;
  page_count: number;
  starter_pages: StarterPage[];
  diagrams: DiagramPlacement[];
  risers: RiserInstruction[];
  selected_scope_ids: string[];
  selected_note_ids: string[];
  selected_keynote_ids: string[];
  scope_of_work_text: string;
  scope_of_work_source_ids: string[];
  cover_attributes: Record<string, string>;
  responsibility_matrix_attributes: Record<string, string>;
  review_items: ReviewItem[];
  network_riser_config: NetworkRiserConfig;
  loudspeaker_riser_config: LoudspeakerRiserConfig;
  quantity_legend_config: QuantityLegendConfig;
  sheet_index_config: SheetIndexConfig;
  scope_of_work_table_config: ScopeOfWorkTableConfig;
  symbol_legend_config: SymbolLegendConfig;
  loudspeaker_schedule_config: LoudspeakerScheduleConfig;
  /** Present only on a Palm Beach County project. */
  zones_table_config?: ZonesTableConfig;
  generation_mode: string;
  /** Web only: the plugin does not read these yet. */
  cutover: boolean;
  zone_attachments: ZoneAttachmentEntry[];
  zone_attachment_blocks: { terminal_block: string; surge_protector: string };
}

/** json.dumps(..., indent=2, ensure_ascii=True) plus the trailing newline, the way the desktop app saves a package. */
export function serializePackage(manifest: BuildManifest): string {
  const escaped = JSON.stringify(manifest, null, 2).replace(
    /[\u007f-￿]/g,
    (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, "0")}`,
  );
  return `${escaped}\n`;
}
